/**
 * API client for the image-search surface.
 *
 * Two calls, both keyed on the library id (images are raw library assets, no
 * doc_type scope):
 *   - listImages → GET /api/libraries/{id}/images  (metadata: title/file/…)
 *   - image bytes → the EXISTING GET /api/find-image?caption=…&library_id=…
 *     proxy (cached on the server). An <img> tag can't carry the Bearer header,
 *     so we fetch with the header in JS and hand back an object URL (thumbnail)
 *     or a data-URI (document insertion).
 *
 * Mirrors the auth pattern in client.ts (getSessionToken + Bearer, API_BASE).
 */

import { getSessionToken } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";

export interface ImageItem {
  /** Caption — both the label shown to the writer and the /api/find-image key. */
  title: string;
  file_id: string;
  file_name: string;
}

export interface ImageListResult {
  library_id: string;
  images: ImageItem[];
  files: { file_id: string; file_name: string }[];
}

function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** List the library's figures, optionally narrowed to one file / caption keyword. */
export async function listImages(
  libraryId: string,
  opts: { fileId?: string | null; q?: string | null } = {},
): Promise<ImageListResult> {
  const params = new URLSearchParams();
  if (opts.fileId) params.set("file_id", opts.fileId);
  if (opts.q) params.set("q", opts.q);
  const qs = params.toString();
  const resp = await fetch(`${API_BASE}/libraries/${encodeURIComponent(libraryId)}/images${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ImageListResult>;
}

/** Fetch one figure's bytes via the cached /api/find-image proxy. */
async function fetchImageBlob(libraryId: string, title: string): Promise<Blob> {
  const resp = await fetch(
    `${API_BASE}/find-image?caption=${encodeURIComponent(title)}&library_id=${encodeURIComponent(libraryId)}`,
    { credentials: "include", headers: authHeaders() },
  );
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.blob();
}

/** Object URL for an <img> thumbnail. Caller must URL.revokeObjectURL on unmount. */
export async function fetchImageObjectUrl(libraryId: string, title: string): Promise<string> {
  return URL.createObjectURL(await fetchImageBlob(libraryId, title));
}

/** Data-URI for inserting the figure into Word (Office.js wants base64). */
export async function fetchImageDataUri(libraryId: string, title: string): Promise<string> {
  const blob = await fetchImageBlob(libraryId, title);
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("could not read image bytes"));
    reader.readAsDataURL(blob);
  });
}
